import {
    useMutation as useTanStackMutation,
    useQuery as useTanStackQuery,
    useQueryClient,
} from '@tanstack/react-query';
import {StatusCodes} from 'http-status-codes';
import {useUserContext} from '../context';

export class EndpointError extends Error {
    #cause;

    constructor(message, cause) {
        super(message);

        this.#cause = cause;
    }

    get cause() {
        return this.#cause;
    }
}

function useAuthenticatedFetch() {
    const {authenticatedFetch} = useUserContext();

    return async (endpoint, method, body) => {
        const response = await authenticatedFetch(endpoint, {
            body: body ? JSON.stringify(body) : null,
            method: method,
        });

        if (response.ok) {
            /* eslint-disable no-undefined */
            return response.status !== StatusCodes.NO_CONTENT
                ? response.headers.get('content-type')?.includes('application/json')
                    ? await response.json()
                    : await response.text()
                : undefined;
            /* eslint-enable no-undefined */
        }

        let errorData = '';
        try {
            errorData = await response.clone().json();
        }
        catch (_err) {
            errorData = await response.text();
        }

        const errorCause = {
            errorData: errorData,
            status: response.status,
        };

        throw new EndpointError(`Error response ${response.status} received from fetch`, errorCause);
    };
}

export function useQuery(options) {
    const {
        cacheKey,
        endpoint,
        method = 'GET',
        ...queryFnArgs
    } = options;

    const fetchFn = useAuthenticatedFetch();
    return useTanStackQuery({
        /* eslint-disable @tanstack/query/exhaustive-deps */
        queryKey: cacheKey ?? [endpoint],
        queryFn: async () => fetchFn(endpoint, method),
        ...queryFnArgs,
        /* eslint-enable @tanstack/query/exhaustive-deps */
    });
}

export function useLazyQuery(options) {
    const fetchFn = useAuthenticatedFetch();
    const queryClient = useQueryClient();

    const lazyQueryFn = async (queryFnOptions = {}) => {
        const {
            cacheKey,
            endpoint,
            method = 'GET',
            ...queryFnArgs
        } = {
            ...options,
            ...queryFnOptions,
        };

        return queryClient.fetchQuery({
            queryKey: cacheKey ?? [endpoint],
            queryFn: async () => fetchFn(endpoint, method),
            ...queryFnArgs,
        });
    };

    return [lazyQueryFn];
}

export function useMutation(options) {
    const {
        endpoint,
        invalidateQuery = false,
        updateCache,
        updateCacheKey,
        ...mutationFnArgs
    } = options;

    const fetchFn = useAuthenticatedFetch();
    const queryClient = useQueryClient();
    const mutationDetails = useTanStackMutation({
        mutationFn: async (variables) => {
            const {
                body,
                method = 'POST',
            } = variables ?? {};

            return fetchFn(endpoint, method, body);
        },
        onSuccess: (data) => {
            // Either use the supplied cache key, or just use the endpoint
            const cacheKey = updateCacheKey ?? [endpoint];

            if (updateCache) {
                // If we invalidate the query AND update the cache, it can mean a corresponding GET will happen, which
                // we don't necessarily want. I.e. when creating a profile, we shouldn't need to GET the profile on each
                // question, as ihe cache should get updated when we POST the previous answer.
                if (invalidateQuery) {
                    // Invalidate the query AND update the value in the cache
                    queryClient
                        .invalidateQueries({queryKey: cacheKey})
                        .then(() => {
                            queryClient.setQueryData(cacheKey, data);
                        });
                }
                else {
                    // Just update the cache
                    queryClient.setQueryData(cacheKey, data);
                }
            }
            else if (invalidateQuery) {
                queryClient.invalidateQueries({queryKey: cacheKey});
            }
        },
        ...mutationFnArgs,
    });

    return {
        ...mutationDetails,
        queryClient,
    };
}
